import { AlertCircle, Calendar, Check, Copy, Loader2, X } from 'lucide-react';
import { Button } from '@/ui/button';
import { DateTimePicker } from '@/ui/datetime-picker';
import { Input } from '@/ui/input';
import type { CreateApiKeyResponse } from '@/services/api-key';

interface ProfileCenterApiKeyCreateModalProps {
  isOpen: boolean;
  creating: boolean;
  newKeyName: string;
  setNewKeyName: (value: string) => void;
  newKeyExpireAt: string;
  setNewKeyExpireAt: (value: string) => void;
  createdKey: CreateApiKeyResponse | null;
  copied: boolean;
  onClose: () => void;
  onCreate: () => void;
  onCopy: (text: string) => void;
  formatDateTime: (timestamp?: number) => string;
}

export function ProfileCenterApiKeyCreateModal({
  isOpen,
  creating,
  newKeyName,
  setNewKeyName,
  newKeyExpireAt,
  setNewKeyExpireAt,
  createdKey,
  copied,
  onClose,
  onCreate,
  onCopy,
  formatDateTime,
}: ProfileCenterApiKeyCreateModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4">
      <div
        className="w-[520px] max-w-[90vw] rounded-lg bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <h2 className="text-lg font-medium text-gray-900">
            {createdKey ? '创建成功' : '创建 API Key'}
          </h2>
          <button
            onClick={onClose}
            className="h-6 w-6 text-gray-400 transition-colors hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {!createdKey ? (
            <div className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  API Key 名称 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={newKeyName}
                  onChange={(event) => setNewKeyName(event.target.value)}
                  placeholder="请输入 API Key 名称，如：生产环境、测试环境"
                  maxLength={50}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  建议填写有意义的名称以便区分不同用途的 API Key
                </p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  有效截止日期 <span className="text-red-500">*</span>
                </label>
                <DateTimePicker
                  value={newKeyExpireAt}
                  onChange={setNewKeyExpireAt}
                  placeholder="请选择过期时间"
                  minDate={new Date()}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  到达截止日期后，该 API Key 将自动失效
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">请立即保存您的 API Key</p>
                    <p className="mt-1">
                      完整的 Key 仅在创建时显示一次，关闭弹窗后将无法再次查看。
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  API Key
                </label>
                <div className="flex gap-2">
                  <div className="min-h-[38px] flex-1 break-all rounded-md border border-gray-200 bg-gray-100 px-3 py-2 font-mono text-sm text-gray-700">
                    {createdKey.plain_key || '（未获取到 Key）'}
                  </div>
                  <Button
                    onClick={() => {
                      if (createdKey.plain_key) {
                        onCopy(createdKey.plain_key);
                      }
                    }}
                    className="gap-1.5 text-white"
                    disabled={!createdKey.plain_key}
                    style={{
                      background: copied
                        ? '#10B981'
                        : 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
                    }}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    {copied ? '已复制' : '复制'}
                  </Button>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  名称
                </label>
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {createdKey.name}
                </div>
              </div>

              {createdKey.expires_at && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    有效截止日期
                  </label>
                  <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatDateTime(createdKey.expires_at)}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
          {!createdKey ? (
            <>
              <Button
                variant="outline"
                className="h-[38px] w-20"
                onClick={onClose}
              >
                取消
              </Button>
              <Button
                className="h-[38px] w-20 text-white"
                style={{
                  background:
                    'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
                }}
                onClick={onCreate}
                disabled={creating || !newKeyName.trim() || !newKeyExpireAt}
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  '创建'
                )}
              </Button>
            </>
          ) : (
            <Button
              className="h-[38px] w-24 text-white"
              style={{
                background: 'linear-gradient(135deg, #7bb8ff 0%, #3F3663 100%)',
              }}
              onClick={onClose}
            >
              完成
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
